import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ChevronRight } from 'lucide-react';
import Footer from '../components/Footer';

const Terms = () => {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState<string | null>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const sections = [
    { id: 'description', title: '1. Description of Service' },
    { id: 'eligibility', title: '2. Eligibility' },
    { id: 'account', title: '3. Account and Access' },
    { id: 'acceptable-use', title: '4. Acceptable Use' },
    { id: 'data-usage', title: '5. Data Usage and Privacy' },
    { id: 'integrations', title: '6. Integration with Third-Party Services' },
    { id: 'ip', title: '7. Intellectual Property' },
    { id: 'termination', title: '8. Termination' },
    { id: 'disclaimers', title: '9. Disclaimers' },
    { id: 'liability', title: '10. Limitation of Liability' },
    { id: 'modifications', title: '11. Modifications' },
    { id: 'contact', title: '12. Contact' },
  ];

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setActiveSection(sectionId);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a111e] flex flex-col">
      <div className="absolute inset-0 overflow-visible">
        <div className="absolute top-1/4 -left-1/4 w-[600px] h-[600px] bg-pink-500/20 rounded-full blur-[180px] animate-pulse" />
        <div className="absolute -bottom-1/4 -right-1/4 w-[600px] h-[600px] bg-blue-500/10 rounded-full blur-[180px] animate-pulse-slow" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-purple-500/5 rounded-full blur-[200px] animate-pulse-slower" />
      </div>

      <button
        onClick={() => navigate('/')}
        className="fixed top-8 left-8 z-20 flex items-center gap-2 px-4 py-2 bg-[#0F1824]/80 backdrop-blur-sm rounded-lg border border-white/[0.05] text-white hover:border-pink-500/50 transition-all duration-300 group hover:bg-[#0F1824]"
      >
        <ArrowLeft className="h-5 w-5 text-pink-500 group-hover:transform group-hover:-translate-x-1 transition-transform" />
        <span>Back</span>
      </button>

      <main className="flex-1 relative z-10 py-16 px-4">
        <div className={`max-w-6xl mx-auto mt-12 flex gap-12 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="hidden lg:block w-64 shrink-0">
            <div className="sticky top-32">
              <h3 className="text-lg font-semibold text-white mb-4 bg-gradient-to-r from-pink-500 to-purple-500 bg-clip-text text-transparent">Table of Contents</h3>
              <nav className="space-y-2">
                {sections.map((section) => (
                  <button
                    key={section.id}
                    onClick={() => scrollToSection(section.id)}
                    className={`w-full text-left px-4 py-2 rounded-lg transition-all duration-300 flex items-center gap-2 group backdrop-blur-sm ${
                      activeSection === section.id
                        ? 'bg-pink-500/10 text-pink-500 border border-pink-500/20'
                        : 'text-gray-400 hover:text-white hover:bg-white/[0.05] border border-transparent'
                    }`}
                  >
                    <ChevronRight className={`h-4 w-4 transition-transform duration-300 ${
                      activeSection === section.id ? 'rotate-90' : 'group-hover:translate-x-0.5'
                    }`} />
                    {section.title}
                  </button>
                ))}
              </nav>
            </div>
          </div>

          <div className="flex-1">
            <div className="prose prose-invert max-w-none">
              <div className="bg-[#0F1824]/80 backdrop-blur-sm rounded-2xl border border-white/[0.05] p-8 md:p-12 space-y-12 shadow-2xl">
                <header className="text-center mb-12">
                  <h1 className="text-4xl md:text-5xl font-bold text-white mb-4 bg-gradient-to-r from-pink-500 via-purple-500 to-blue-500 bg-clip-text text-transparent animate-gradient">
                    Terms of Service
                  </h1>
                  <p className="text-gray-400">Last Updated: April 10, 2025</p>
                </header>

                <section className="space-y-4">
                  <p className="text-gray-300 leading-relaxed">
                    Welcome to TiccAI, a service provided by TiccAI Inc. ("TiccAI", "we", "our", or "us"). These Terms of Service ("Terms") govern your access to and use of TiccAI's platform, including our software, integrations, services, websites, and any related documentation (collectively, the "Service").
                  </p>
                  <p className="text-gray-300 leading-relaxed">
                    By accessing or using the Service, you agree to be bound by these Terms. If you are using the Service on behalf of an organization, you agree to these Terms on behalf of that organization and warrant that you have the authority to do so.
                  </p>
                </section>

                <section id="description" className="space-y-4">
                  <h2 className="text-2xl font-semibold text-white mb-4">1. Description of Service</h2>
                  <p className="text-gray-300">TiccAI simplifies on-call ticket handling by:</p>
                  <ul className="list-none space-y-3 mt-4">
                    <li className="flex items-start gap-3">
                      <span className="text-pink-500 mt-1">•</span>
                      <span className="text-gray-300">Automating responses using your internal documentation and live system data</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-pink-500 mt-1">•</span>
                      <span className="text-gray-300">Surfacing relevant, contextual information for faster triage</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-pink-500 mt-1">•</span>
                      <span className="text-gray-300">Grouping similar tickets together</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-pink-500 mt-1">•</span>
                      <span className="text-gray-300">Integrating seamlessly with your existing tools (e.g., Slack, email, internal portals) into a unified dashboard</span>
                    </li>
                  </ul>
                  <p className="text-gray-300 mt-6">
                    Our platform is built to alleviate the burden on on-call teams by drawing insights from Confluence, GitHub, internal APIs, and live service metrics to either auto-resolve issues or assist engineers in diagnosing problems more efficiently.
                  </p>
                </section>

                <section id="eligibility" className="space-y-4">
                  <h2 className="text-2xl font-semibold text-white mb-4">2. Eligibility</h2>
                  <p className="text-gray-300 leading-relaxed">
                    You must be at least 18 years old and capable of entering into a legally binding agreement to use our Service. If you are using the Service on behalf of a company or other legal entity, you represent that you have the authority to bind that entity to these Terms.
                  </p>
                </section>

                <section id="account" className="space-y-4">
                  <h2 className="text-2xl font-semibold text-white mb-4">3. Account and Access</h2>
                  <p className="text-gray-300 leading-relaxed">
                    You are responsible for maintaining the confidentiality of your login credentials and are fully responsible for all activities that occur under your account. You agree to notify us immediately of any unauthorized use of your account.
                  </p>
                </section>

                <section id="acceptable-use" className="space-y-4">
                  <h2 className="text-2xl font-semibold text-white mb-4">4. Acceptable Use</h2>
                  <p className="text-gray-300 leading-relaxed">You agree not to:</p>
                  <ul className="list-none space-y-3 mt-4">
                    <li className="flex items-start gap-3">
                      <span className="text-pink-500 mt-1">•</span>
                      <span className="text-gray-300">Reverse engineer, decompile, or otherwise attempt to discover the source code of the Service</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-pink-500 mt-1">•</span>
                      <span className="text-gray-300">Interfere with or disrupt the integrity or performance of the Service</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-pink-500 mt-1">•</span>
                      <span className="text-gray-300">Use the Service to violate any applicable laws or regulations</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-pink-500 mt-1">•</span>
                      <span className="text-gray-300">Upload or transmit any viruses, malware, or other harmful code</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-pink-500 mt-1">•</span>
                      <span className="text-gray-300">Access the Service in order to build a competing product or service</span>
                    </li>
                  </ul>
                </section>

                <section id="data-usage" className="space-y-4">
                  <h2 className="text-2xl font-semibold text-white mb-4">5. Data Usage and Privacy</h2>
                  <p className="text-gray-300 leading-relaxed">
                    TiccAI accesses and processes the data you provide (such as documentation, ticket messages, and API outputs) solely for the purpose of delivering and improving the Service.
                  </p>
                  <p className="text-gray-300 leading-relaxed">
                    We do not claim ownership of any data you input. You retain all rights to your content, and we only access it in accordance with our Privacy Policy and to enable service functionality (e.g., resolving tickets, surfacing insights, etc.).
                  </p>
                </section>

                <section id="integrations" className="space-y-4">
                  <h2 className="text-2xl font-semibold text-white mb-4">6. Integration with Third-Party Services</h2>
                  <p className="text-gray-300 leading-relaxed">
                    TiccAI supports integrations with tools like Slack, Confluence, GitHub, email systems, and custom internal APIs. By enabling such integrations, you grant us permission to access and interact with the respective data solely to deliver the intended functionality.
                  </p>
                  <p className="text-gray-300 leading-relaxed">
                    You are responsible for complying with any terms associated with the third-party services you connect to TiccAI.
                  </p>
                </section>

                <section id="ip" className="space-y-4">
                  <h2 className="text-2xl font-semibold text-white mb-4">7. Intellectual Property</h2>
                  <p className="text-gray-300 leading-relaxed">
                    All intellectual property rights in the Service (excluding your content) are owned by TiccAI or its licensors. You are granted a limited, non-exclusive, non-transferable license to access and use the Service in accordance with these Terms.
                  </p>
                </section>

                <section id="termination" className="space-y-4">
                  <h2 className="text-2xl font-semibold text-white mb-4">8. Termination</h2>
                  <p className="text-gray-300 leading-relaxed">
                    We reserve the right to suspend or terminate your access to the Service at any time, with or without notice, if you violate these Terms or if we determine it is necessary to protect the integrity of the Service.
                  </p>
                  <p className="text-gray-300 leading-relaxed">
                    You may terminate your use at any time by ceasing all access to the Service. Upon termination, all licenses granted to you will immediately expire.
                  </p>
                </section>

                <section id="disclaimers" className="space-y-4">
                  <h2 className="text-2xl font-semibold text-white mb-4">9. Disclaimers</h2>
                  <p className="text-gray-300 leading-relaxed">
                    The Service is provided "as is" and "as available" without warranties of any kind, either express or implied. TiccAI does not guarantee that the Service will be error-free, uninterrupted, or that it will meet your specific requirements.
                  </p>
                </section>

                <section id="liability" className="space-y-4">
                  <h2 className="text-2xl font-semibold text-white mb-4">10. Limitation of Liability</h2>
                  <p className="text-gray-300 leading-relaxed">
                    To the fullest extent permitted by law, TiccAI shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of the Service.
                  </p>
                </section>

                <section id="modifications" className="space-y-4">
                  <h2 className="text-2xl font-semibold text-white mb-4">11. Modifications</h2>
                  <p className="text-gray-300 leading-relaxed">
                    We may update these Terms from time to time. If we make material changes, we will notify you via the Service or by email. Continued use of the Service after such updates constitutes your acceptance of the new Terms.
                  </p>
                </section>

                <section id="contact" className="space-y-4">
                  <h2 className="text-2xl font-semibold text-white mb-4">12. Contact</h2>
                  <p className="text-gray-300 leading-relaxed">
                    If you have any questions about these Terms, please contact us at:
                  </p>
                  <p className="text-gray-300 leading-relaxed">
                    business@ticcai.com<br />
                  </p>
                </section>

                <div className="mt-12 pt-8 border-t border-white/[0.05]">
                  <p className="text-gray-300 text-sm">
                    By using our service, you acknowledge that you have read and understand these Terms and agree to be bound by them.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Terms;